import type { ReactNode } from 'react';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { CaretDownIcon } from '../../images/shapes';

interface PanelProps {
  /**
   * Test id for Panel
   */
  testId?: string;
  /**
   * Panel title
   */
  title?: ReactNode;
  /**
   * Panel children
   */
  children?: ReactNode;
  /**
   * Callback run on panel opens
   */
  onChange?: () => void;
  /**
   * Boolean for panel to render open
   */
  open?: boolean;
  /**
   * Boolean for blocking panel to open
   */
  isCollapsable?: boolean;
  /**
   * Node with panel tooltip
   */
  helpText?: ReactNode;
  /**
   * Panel width
   */
  width?: string;
  /**
   * Additional classes for children
   */
  additionalChildrenClasses?: string;
  /**
   * Additional classes for panel container
   */
  additionalPanelContainerClasses?: string;
  /**
   * Additional classes for panel title
   */
  additionalTitleClasses?: string;
  /**
   * Additional classes for panel container
   */
  additionalContainerClasses?: string;
  /**
   * Additional classes for children container
   */
  additionalChildrenContainerClasses?: string;
  /**
   * Additional classes for panel icon
   */
  additionalIconClasses?: string;
  /**
   * Additional classes for panel title container
   */
  additionalTitleWrapClasses?: string;
  /**
   * Panel id
   */
  id?: string;
}

const Panel = ({
  title = '',
  children = '',
  onChange = EMPTY_FUNCTION,
  open = false,
  helpText = '',
  width = 'auto',
  isCollapsable = true,
  additionalTitleWrapClasses = '',
  additionalTitleClasses = '',
  additionalChildrenClasses = '',
  additionalContainerClasses = '',
  additionalChildrenContainerClasses = '',
  additionalIconClasses = '',
  testId = '',
  id = '',
}: PanelProps) => {
  const onPanelOpen = useCallback(() => {
    if (onChange && isCollapsable) {
      onChange();
    }
  }, [onChange, isCollapsable]);

  return (
    <Disclosure defaultOpen={open}>
      {({ open }) => (
        <div
          style={{ width: width }}
          className={twMerge(
            'px-7 py-5 rounded-lg bg-white dark:bg-slate-950 relative h-fit',
            additionalContainerClasses,
          )}
          id={id}
          {...getTestProps(testId, `panel-container`)}
        >
          <DisclosureButton
            onClick={onPanelOpen}
            disabled={!isCollapsable}
            as="div"
            className={twMerge(
              'flex items-center justify-between',
              isCollapsable && 'cursor-pointer',
              additionalTitleClasses,
            )}
            {...getTestProps(testId, `panel-open`)}
          >
            <div
              className={twMerge(
                'flex justify-center items-center',
                additionalTitleWrapClasses,
              )}
            >
              <span
                className={twMerge(
                  'mr-2 font-bold text-base dark:text-white',
                  additionalTitleClasses,
                )}
                {...getTestProps(testId, `panel-title`)}
              >
                {title}
              </span>
              {helpText}
            </div>
            {isCollapsable && (
              <CaretDownIcon
                className={twMerge(
                  'w-3 min-w-3 transition-transform duration-300 ease-in-out dark:text-white',
                  additionalIconClasses,
                  open && 'rotate-180',
                )}
                {...getTestProps(testId, `panel-open-icon`)}
              />
            )}
          </DisclosureButton>
          <DisclosurePanel
            className={twMerge(
              'overflow-visible pt-4 dark:text-white',
              'duration-100 ease-out data-closed:scale-95 data-closed:opacity-0',
              additionalChildrenClasses,
              additionalChildrenContainerClasses,
            )}
            transition
            {...getTestProps(testId, `panel-content-container`)}
          >
            {children}
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
};
export default Panel;
